// webSearchHandler.js - FIXED VERSION

import { 
  performWebSearchBackend,
  fetchWebContentBackend
} from './backendAPI.js';
/**
 * Real-time Web Search from Reliable RSS Sources
 * Features: Current news only, specific source requests, URL summarization
 */

import { enrichSearchResultsWithImages } from './webExtract.js';

// CORS proxies ordered by reliability
const CORS_PROXIES = [
  "https://api.allorigins.win/raw?url=",
  "https://corsproxy.io/?",
  "https://api.codetabs.com/v1/proxy?quest="
];

/**
 * Check if article is recent (within last 7 days) - FIXED
 */
const isRecentArticle = (dateString) => {
  try {
    const articleDate = new Date(dateString);
    const now = new Date();
    const diffMs = now - articleDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return articleDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (e) {
    return 'Recently';
  }
};
/**
 * RELIABLE NEWS SOURCES - Only sources with consistent RSS image feeds
 */
const NEWS_SOURCES = {
  // TIER 1: Major News Networks
  majorNews: [
    {
      name: 'BBC News - Top Stories',
      url: 'http://feeds.bbci.co.uk/news/rss.xml',
      categories: ['news', 'breaking', 'headline', 'world', 'uk', 'latest', 'current'],
      aliases: ['bbc']
    },
    
    {
      name: 'BBC News - Technology',
      url: 'http://feeds.bbci.co.uk/news/technology/rss.xml',
      categories: ['tech', 'technology', 'digital', 'cyber', 'innovation', 'ai', 'software'],
      aliases: ['bbc tech', 'bbc technology']
    },
    {
      name: 'BBC News - Business',
      url: 'http://feeds.bbci.co.uk/news/business/rss.xml',
      categories: ['business', 'economy', 'finance', 'market', 'trade', 'corporate'],
      aliases: ['bbc business']
    },
    {
      name: 'BBC News - Science',
      url: 'http://feeds.bbci.co.uk/news/science_and_environment/rss.xml',
      categories: ['science', 'research', 'environment', 'climate', 'space', 'discovery'],
      aliases: ['bbc science']
    },
    {
      name: 'ABC News - Top Stories',
      url: 'https://abcnews.go.com/abcnews/topstories',
      categories: ['news', 'breaking', 'usa', 'america', 'national', 'headline'],
      aliases: ['abc', 'abc news']
    },
    {
      name: 'CNN - Top Stories',
      url: 'http://rss.cnn.com/rss/edition.rss',
      categories: ['news', 'breaking', 'headline', 'world', 'international'],
      aliases: ['cnn']
    },
    {
      name: 'NPR News',
      url: 'https://feeds.npr.org/1001/rss.xml',
      categories: ['news', 'npr', 'national', 'usa', 'politics', 'culture'],
      aliases: ['npr']
    }
  ],

  // TIER 2: Business & Finance
  business: [
    {
      name: 'Reuters Business',
      url: 'https://feeds.reuters.com/reuters/businessNews',
      categories: ['business', 'finance', 'economy', 'corporate', 'market', 'earnings'],
      aliases: ['reuters', 'reuters business']
    },
    {
      name: 'Reuters Technology',
      url: 'https://feeds.reuters.com/reuters/technologyNews',
      categories: ['tech', 'technology', 'innovation', 'digital', 'software'],
      aliases: ['reuters tech']
    },
    {
      name: 'Bloomberg Markets',
      url: 'https://feeds.bloomberg.com/markets/news.rss',
      categories: ['finance', 'market', 'stock', 'trading', 'investment', 'wall street'],
      aliases: ['bloomberg']
    },
    {
      name: 'CNBC',
      url: 'https://www.cnbc.com/id/100003114/device/rss/rss.html',
      categories: ['business', 'finance', 'market', 'economy', 'investing'],
      aliases: ['cnbc']
    }
  ],

  // TIER 3: Technology
  technology: [
    {
      name: 'TechCrunch',
      url: 'https://techcrunch.com/feed/',
      categories: ['tech', 'startup', 'ai', 'funding', 'venture', 'silicon valley', 'saas'],
      aliases: ['techcrunch']
    },
    {
      name: 'The Verge',
      url: 'https://www.theverge.com/rss/index.xml',
      categories: ['tech', 'gadgets', 'reviews', 'consumer', 'apple', 'google', 'microsoft'],
      aliases: ['verge', 'the verge']
    },
    {
      name: 'Wired',
      url: 'https://www.wired.com/feed/rss',
      categories: ['tech', 'science', 'innovation', 'future', 'ai', 'security'],
      aliases: ['wired']
    },
    {
      name: 'Ars Technica',
      url: 'https://feeds.arstechnica.com/arstechnica/index',
      categories: ['tech', 'science', 'policy', 'gadgets', 'gaming', 'security'],
      aliases: ['ars technica', 'arstechnica']
    }
  ],

  // TIER 4: Science & Health
  science: [
    {
      name: 'ScienceDaily',
      url: 'https://www.sciencedaily.com/rss/all.xml',
      categories: ['science', 'biology', 'research', 'medical', 'health', 'study'],
      aliases: ['sciencedaily', 'science daily']
    }
  ]
};

/**
 * Detect if query is requesting a specific news source
 */
const detectSpecificSource = (query) => {
  const queryLower = query.toLowerCase();
  
  // Flatten all sources
  const allSources = [
    ...NEWS_SOURCES.majorNews,
    ...NEWS_SOURCES.business,
    ...NEWS_SOURCES.technology,
    ...NEWS_SOURCES.science
  ];
  
  // Check for explicit source mentions
  for (const source of allSources) {
    if (source.aliases) {
      for (const alias of source.aliases) {
        if (queryLower.includes(alias)) {
          console.log(`🎯 Detected specific source request: ${source.name}`);
          return [source];
        }
      }
    }
  }
  
  return null;
};

/**
 * Detect if query contains a URL
 */
export const detectURL = (query) => {
  if (!query) return null;
  
  // CRITICAL FIX: Ignore site: search operators
  if (query.toLowerCase().includes('site:')) {
    console.log('🔍 site: operator detected - NOT treating as URL');
    return null;
  }
  
  const cleanedQuery = query
    .toLowerCase()
    .replace(/tell me about (this|that|the) (url|link|website|site)/gi, '')
    .replace(/summarize (this|that|the) (url|link|website|site)/gi, '')
    .replace(/what('s| is) (in|on|at) (this|that|the) (url|link|website|site)/gi, '')
    .replace(/what does .+ do/gi, '')
    .replace(/explain .+ (to me)?/gi, '')
    .trim();
  
  // Full URLs with protocol
  const fullUrlPattern = /(https?:\/\/[^\s]+)/gi;
  const fullUrlMatches = cleanedQuery.match(fullUrlPattern);
  if (fullUrlMatches) {
    console.log('🔗 Full URL detected:', fullUrlMatches[0]);
    return fullUrlMatches[0];
  }
  
  // Bare domains - but NOT if part of site: operator
  const domainPattern = /\b([a-z0-9-]+\.)+[a-z]{2,}\b/gi;
  const domainMatches = cleanedQuery.match(domainPattern);
  
  if (domainMatches) {
    const validDomains = domainMatches.filter(domain => {
      const lowerDomain = domain.toLowerCase();
      return !lowerDomain.match(/\.(jpg|jpeg|png|gif|pdf|doc|docx|txt|zip)$/i) &&
             !lowerDomain.match(/^(e\.g\.|i\.e\.|etc\.)$/i);
    });
    
    if (validDomains.length > 0) {
      const url = 'https://' + validDomains[0];
      console.log('🔗 Bare domain detected, converted to:', url);
      return url;
    }
  }
  
  return null;
};


/**
 * Fetch and summarize content from a URL
 */
export const fetchURLContent = async (url) => {
  try {
    console.log(`📄 Fetching content from: ${url}`);
    
    let html = null;
    const proxies = [
      `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
      `https://corsproxy.io/?${encodeURIComponent(url)}`
    ];
    
    for (const proxyUrl of proxies) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(proxyUrl, {
          method: 'GET',
          signal: controller.signal,
          headers: { 'Accept': 'text/html' }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) continue;
        
        html = await response.text();
        if (html && html.length > 0) break;
      } catch (err) {
        continue;
      }
    }
    
    if (!html || html.length === 0) {
      return {
        success: false,
        error: "Couldn't fetch the URL content. The site may be blocking automated access."
      };
    }
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Extract title
    const title = doc.querySelector('title')?.textContent?.trim() ||
                  doc.querySelector('meta[property="og:title"]')?.getAttribute('content') ||
                  'Untitled';
    
    // Extract description/summary
    const description = doc.querySelector('meta[property="og:description"]')?.getAttribute('content') ||
                        doc.querySelector('meta[name="description"]')?.getAttribute('content') ||
                        '';
    
    // Extract main content
    const articleSelectors = [
      'article',
      '[role="main"]',
      '.article-content',
      '.post-content',
      '.entry-content',
      'main'
    ];
    
    let content = '';
    for (const selector of articleSelectors) {
      const element = doc.querySelector(selector);
      if (element) {
        content = element.textContent?.trim() || '';
        if (content.length > 200) break;
      }
    }
    
    // Fallback to body paragraphs
    if (content.length < 200) {
      const paragraphs = Array.from(doc.querySelectorAll('p'))
        .map(p => p.textContent?.trim())
        .filter(text => text && text.length > 50)
        .slice(0, 5)
        .join('\n\n');
      content = paragraphs || content;
    }
    
    // Clean up content
    content = content
      .replace(/\s+/g, ' ')
      .replace(/\n\s*\n/g, '\n\n')
      .trim()
      .substring(0, 2000);
    
    console.log(`✅ Successfully extracted content (${content.length} chars)`);
    
    return {
      success: true,
      title,
      description,
      content,
      url
    };
    
  } catch (error) {
    console.error('❌ URL fetch error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get relevant sources based on query
 */
const getRelevantSources = (query) => {
  const queryLower = query.toLowerCase();
  
  // Check for site-specific requests (e.g., "site:cnn.com")
  const siteMatch = queryLower.match(/site:([a-z0-9.-]+)/);
  
  if (siteMatch) {
    const siteDomain = siteMatch[1];
    console.log(`🎯 Site-specific search detected: ${siteDomain}`);
    
    // Map domains to specific RSS feeds
    const siteToFeed = {
      'cnn.com': NEWS_SOURCES.majorNews.find(s => s.name.includes('CNN')),
      'bbc.com': NEWS_SOURCES.majorNews.find(s => s.name.includes('BBC News - Top')),
      'bbc.com/news': NEWS_SOURCES.majorNews.find(s => s.name.includes('BBC News - Top')),
      'abcnews.go.com': NEWS_SOURCES.majorNews.find(s => s.name.includes('ABC News')),
      'nbcnews.com': NEWS_SOURCES.majorNews.find(s => s.name.includes('NBC')),
      'foxnews.com': NEWS_SOURCES.majorNews.find(s => s.name.includes('Fox')),
      'techcrunch.com': NEWS_SOURCES.technology.find(s => s.name.includes('TechCrunch'))
    };
    
    const feed = siteToFeed[siteDomain];
    if (feed) {
      console.log(`✅ Using RSS feed: ${feed.name}`);
      return [feed];
    }
  }
  
  // Check for specific source request first
  const specificSource = detectSpecificSource(query);
  if (specificSource) {
    return specificSource;
  }
  
  // Rest of the original function...
  const keywords = queryLower.split(/\s+/).filter(w => w.length > 2);
  
  let selectedSources = [];
  const maxSources = 10;
  
  // Always include 2-3 major news sources for broad coverage
  selectedSources.push(
    NEWS_SOURCES.majorNews[0], // BBC Top Stories
    NEWS_SOURCES.majorNews[1], // BBC Technology
    NEWS_SOURCES.majorNews[4]  // ABC News
  );
  
  // Find specialized matches
  const allSources = [
    ...NEWS_SOURCES.majorNews,
    ...NEWS_SOURCES.technology,
    ...NEWS_SOURCES.business,
    ...NEWS_SOURCES.science
  ];
  
  const scoredSources = allSources.map(source => {
    const matchCount = source.categories.filter(cat => 
      keywords.some(keyword => 
        keyword.includes(cat) || 
        cat.includes(keyword) ||
        cat.split(' ').some(catWord => keyword.includes(catWord))
      )
    ).length;
    
    return { source, score: matchCount };
  }).filter(item => item.score > 0);
  
  scoredSources.sort((a, b) => b.score - a.score);
  
  scoredSources.forEach(({ source }) => {
    if (selectedSources.length >= maxSources) return;
    if (!selectedSources.find(s => s.name === source.name)) {
      selectedSources.push(source);
    }
  });
  
  // Fill remaining slots
  if (selectedSources.length < maxSources) {
    const fillerSources = [
      NEWS_SOURCES.technology[0],
      NEWS_SOURCES.business[0],
      NEWS_SOURCES.science[0]
    ];
    
    fillerSources.forEach(source => {
      if (selectedSources.length >= maxSources) return;
      if (!selectedSources.find(s => s.name === source.name)) {
        selectedSources.push(source);
      }
    });
  }
  
  console.log(`🎯 Selected ${selectedSources.length} sources for "${query}":`, 
    selectedSources.map(s => s.name).join(', '));
  
  return selectedSources;
};
/**
 * Fetch RSS feed with proxy and timeout
 */
async function fetchWithProxy(url, timeoutMs = 5000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  let lastError;
  
  for (const proxy of CORS_PROXIES) {
    try {
      const response = await fetch(proxy + encodeURIComponent(url), {
        method: 'GET',
        headers: {
          'Accept': 'application/rss+xml, application/xml, text/xml, */*',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      return await response.text();
      
    } catch (error) {
      lastError = error;
      if (error.name === 'AbortError') {
        throw new Error('Timeout');
      }
      continue;
    }
  }
  
  clearTimeout(timeoutId);
  throw lastError || new Error('All proxies failed');
}

/**
 * Parse RSS feed with robust XML parsing - FIXED DATE HANDLING
 */
const parseRSSFeed = (text, sourceName) => {
  const items = [];
  
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(text, 'text/xml');
    
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      console.warn(`XML parse error for ${sourceName}, trying regex fallback`);
      return parseRSSFeedRegex(text, sourceName);
    }
    
    const xmlItems = xmlDoc.querySelectorAll('item, entry');
    
    xmlItems.forEach(item => {
      try {
        const title = item.querySelector('title')?.textContent?.trim();
        const link = item.querySelector('link')?.textContent?.trim() || 
                     item.querySelector('link')?.getAttribute('href');
        const description = item.querySelector('description, summary, content, content\\:encoded')?.textContent?.trim();
        const pubDateElement = item.querySelector('pubDate, published, updated');
        
        if (!title) return;
        
        // CRITICAL FIX: Properly parse date
        let datePublished = new Date().toISOString();
        if (pubDateElement) {
          try {
            const dateText = pubDateElement.textContent.trim();
            const parsedDate = new Date(dateText);
            
            // Validate the date is actually valid
            if (!isNaN(parsedDate.getTime())) {
              datePublished = parsedDate.toISOString();
            } else {
              console.warn(`Invalid date for ${sourceName}:`, dateText);
            }
          } catch (e) {
            console.error(`Date parse error for ${sourceName}:`, e);
          }
        }
        
        // ONLY add if article is recent
        if (isRecentArticle(datePublished)) {
          items.push({
            title: title.replace(/<[^>]*>/g, '')
                       .replace(/&quot;/g, '"')
                       .replace(/&amp;/g, '&')
                       .replace(/&lt;/g, '<')
                       .replace(/&gt;/g, '>'),
            url: link || '#',
            snippet: description ? 
              description.replace(/<[^>]*>/g, '').substring(0, 200) : 
              title.substring(0, 200),
            source: sourceName,
            datePublished,
            xmlItem: item
          });
        } else {
          console.log(`⏭️ Skipping old article from ${sourceName}: ${new Date(datePublished).toDateString()}`);
        }
      } catch (e) {
        console.warn(`Error parsing item in ${sourceName}:`, e);
      }
    });
    
  } catch (e) {
    console.error(`XML parsing failed for ${sourceName}:`, e);
    return parseRSSFeedRegex(text, sourceName);
  }
  
  return items;
};

/**
 * Regex-based RSS parsing fallback
 */
const parseRSSFeedRegex = (text, sourceName) => {
  const items = [];
  
  try {
    const entries = text.split(/<item>|<entry>/i);
    
    for (let i = 1; i < entries.length && items.length < 50; i++) {
      try {
        const titleMatch = entries[i].match(/<title>(?:<!\[CDATA\[)?(.*?)(?:\]\]>)?<\/title>/is);
        const linkMatch = entries[i].match(/<link[^>]*>(?:<!\[CDATA\[)?(.*?)(?:\]\]>)?<\/link>/is);
        const descMatch = entries[i].match(/<(?:description|summary|content)>(?:<!\[CDATA\[)?(.*?)(?:\]\]>)?<\/(?:description|summary|content)>/is);
        const pubDateMatch = entries[i].match(/<(?:pubDate|published|updated)>(.*?)<\/(?:pubDate|published|updated)>/i);
        
        if (titleMatch) {
          const title = titleMatch[1].trim()
            .replace(/<[^>]*>/g, '')
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&');
          const url = linkMatch ? linkMatch[1].trim() : '#';
          const snippet = descMatch ? 
            descMatch[1].replace(/<[^>]*>/g, '').substring(0, 200) : 
            title.substring(0, 200);
          
          let datePublished = new Date().toISOString();
          if (pubDateMatch) {
            try {
              const parsedDate = new Date(pubDateMatch[1].trim());
              if (!isNaN(parsedDate.getTime())) {
                datePublished = parsedDate.toISOString();
              }
            } catch (e) {}
          }
          
          if (isRecentArticle(datePublished)) {
            items.push({ 
              title, 
              url, 
              snippet, 
              source: sourceName, 
              datePublished,
              xmlItem: null
            });
          }
        }
      } catch (e) {
        continue;
      }
    }
  } catch (e) {
    console.error(`Regex parsing failed for ${sourceName}:`, e);
  }
  
  return items;
};

/**
 * Extract YouTube video ID from URL
 */
export const extractYouTubeId = (url) => {
  if (!url) return null;
  
  try {
    const shortMatch = url.match(/youtu\.be\/([^?&#]+)/);
    if (shortMatch) return decodeURIComponent(shortMatch[1]);
    
    const longMatch = url.match(/[?&]v=([^&#]+)/);
    if (longMatch) return decodeURIComponent(longMatch[1]);
    
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&#]+)/);
    if (embedMatch) return decodeURIComponent(embedMatch[1]);
    
    return null;
  } catch (error) {
    console.error('❌ YouTube ID extraction error:', error);
    return null;
  }
};

/**
 * Check if URL is YouTube
 */
export const isYouTubeURL = (url) => {
  if (!url) return false;
  return /youtube\.com\/watch|youtu\.be\/|youtube\.com\/embed/i.test(url);
};

/**
 * Fetch YouTube video metadata using oEmbed API
 */
export const fetchYouTubeMetadata = async (videoId) => {
  if (!videoId) return null;
  
  try {
    console.log('🎬 Fetching YouTube metadata for:', videoId);
    
    const response = await fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
    );
    
    if (!response.ok) {
      console.error('❌ YouTube oEmbed failed:', response.status);
      return null;
    }
    
    const data = await response.json();
    
    console.log('✅ YouTube metadata fetched:', {
      title: data.title,
      author: data.author_name
    });
    
    return {
      title: data.title,
      author: data.author_name,
      thumbnail: data.thumbnail_url,
      videoId: videoId
    };
    
  } catch (error) {
    console.error('❌ Error fetching YouTube metadata:', error);
    return null;
  }
};

/**
 * Enhanced description scraping with multiple fallbacks
 */
export const scrapeYouTubeDescription = async (videoId) => {
  try {
    console.log('📄 Scraping enhanced description for:', videoId);
    
    const proxyUrl = 'https://api.allorigins.win/get?url=';
    const youtubeUrl = encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`);
    
    const response = await fetch(proxyUrl + youtubeUrl);
    const data = await response.json();
    const html = data.contents;
    
    // Method 1: Meta description
    const descMatch = html.match(/<meta name="description" content="([^"]+)"/);
    if (descMatch) {
      console.log('✅ Description extracted (meta tag)');
      return descMatch[1];
    }
    
    // Method 2: OG description
    const ogDescMatch = html.match(/<meta property="og:description" content="([^"]+)"/);
    if (ogDescMatch) {
      console.log('✅ Description extracted (og tag)');
      return ogDescMatch[1];
    }
    
    // Method 3: Extract from ytInitialData (contains more info)
    const ytDataMatch = html.match(/var ytInitialData = ({.+?});/);
    if (ytDataMatch) {
      try {
        const ytData = JSON.parse(ytDataMatch[1]);
        
        // Try to get full description
        const contents = ytData?.contents?.twoColumnWatchNextResults?.results?.results?.contents;
        if (contents && contents.length > 1) {
          const descContent = contents[1]?.videoSecondaryInfoRenderer?.attributedDescription;
          if (descContent?.content) {
            console.log('✅ Full description extracted (ytInitialData)');
            return descContent.content;
          }
        }
      } catch (e) {
        console.log('⚠️ Could not parse ytInitialData for description');
      }
    }
    
    console.log('⚠️ No description found');
    return null;
  } catch (error) {
    console.error('❌ Description scraping failed:', error);
    return null;
  }
};

/**
 * Extract video category/keywords from page
 */
export const extractVideoMetadata = async (videoId) => {
  try {
    console.log('🔍 Extracting additional metadata for:', videoId);
    
    const proxyUrl = 'https://api.allorigins.win/get?url=';
    const youtubeUrl = encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`);
    
    const response = await fetch(proxyUrl + youtubeUrl);
    const data = await response.json();
    const html = data.contents;
    
    // Extract keywords
    const keywordsMatch = html.match(/<meta name="keywords" content="([^"]+)"/);
    const keywords = keywordsMatch ? keywordsMatch[1] : null;
    
    // Extract category
    const categoryMatch = html.match(/"category":"([^"]+)"/);
    const category = categoryMatch ? categoryMatch[1] : null;
    
    // Extract upload date
    const dateMatch = html.match(/"uploadDate":"([^"]+)"/);
    const uploadDate = dateMatch ? dateMatch[1] : null;
    
    // Extract duration
    const durationMatch = html.match(/"lengthSeconds":"(\d+)"/);
    const duration = durationMatch ? parseInt(durationMatch[1]) : null;
    
    console.log('✅ Metadata extracted:', { keywords: !!keywords, category, duration });
    
    return {
      keywords: keywords ? keywords.split(',').map(k => k.trim()) : null,
      category,
      uploadDate,
      duration,
      durationFormatted: duration ? formatDuration(duration) : null
    };
    
  } catch (error) {
    console.error('❌ Metadata extraction failed:', error);
    return null;
  }
};

/**
 * Format duration in seconds to readable format
 */
const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Generate intelligent content summary based on title, description, and metadata
 */
const generateIntelligentSummary = (title, description, metadata) => {
  const keywords = metadata?.keywords || [];
  const category = metadata?.category || 'general';
  const duration = metadata?.durationFormatted || 'unknown duration';
  
  // Analyze title for topic inference
  const titleLower = title.toLowerCase();
  
  let topicContext = '';
  
  // Detect common video types
  if (titleLower.includes('tutorial') || titleLower.includes('how to')) {
    topicContext = 'This appears to be an instructional tutorial video';
  } else if (titleLower.includes('review')) {
    topicContext = 'This is a review video';
  } else if (titleLower.includes('demo') || titleLower.includes('demonstration')) {
    topicContext = 'This video demonstrates';
  } else if (titleLower.includes('announcement') || titleLower.includes('introducing')) {
    topicContext = 'This is an announcement video introducing';
  } else if (titleLower.includes('interview')) {
    topicContext = 'This is an interview video';
  } else if (titleLower.includes('explained') || titleLower.includes('explanation')) {
    topicContext = 'This video explains';
  } else {
    topicContext = 'This video covers';
  }
  
  // Build rich context from keywords
  let keywordContext = '';
  if (keywords && keywords.length > 0) {
    const relevantKeywords = keywords.slice(0, 5).join(', ');
    keywordContext = `\n\nKey topics: ${relevantKeywords}`;
  }
  
  return {
    topicContext,
    keywordContext,
    category,
    duration
  };
};

/**
 * Get complete YouTube data (without transcript)
 * But with MAXIMUM available information
 */
export const getCompleteYouTubeData = async (videoId) => {
  console.log('🎬 Fetching complete YouTube data for:', videoId);
  
  // Fetch basic metadata
  const metadata = await fetchYouTubeMetadata(videoId);
  if (!metadata) {
    return {
      success: false,
      error: 'Could not fetch video metadata. Video may be private or deleted.'
    };
  }
  
  // Fetch enhanced description
  const description = await scrapeYouTubeDescription(videoId);
  
  // Fetch additional metadata (category, keywords, duration)
  const extraMetadata = await extractVideoMetadata(videoId);
  
  // Generate intelligent summary
  const summary = generateIntelligentSummary(metadata.title, description, extraMetadata);
  
  // Build rich, contextual content
  const content = `YouTube Video Analysis:

Title: "${metadata.title}"
Creator: ${metadata.author}
${extraMetadata?.category ? `Category: ${extraMetadata.category}` : ''}
${extraMetadata?.durationFormatted ? `Duration: ${extraMetadata.durationFormatted}` : ''}
${extraMetadata?.uploadDate ? `Uploaded: ${new Date(extraMetadata.uploadDate).toLocaleDateString()}` : ''}

Description:
${description || 'No description available'}
${summary.keywordContext}

Analysis:
${summary.topicContext} "${metadata.title}". Based on the title, description, and category, this video likely discusses topics related to ${metadata.title.toLowerCase()}.${extraMetadata?.keywords ? ` The video focuses on: ${extraMetadata.keywords.slice(0, 3).join(', ')}.` : ''}

Note: Full video transcript is not available. This summary is based on the video's metadata, title, and description.`;
  
  console.log('✅ Complete YouTube data assembled:', {
    title: metadata.title,
    hasDescription: !!description,
    hasKeywords: !!(extraMetadata?.keywords),
    category: extraMetadata?.category
  });
  
  return {
    success: true,
    title: metadata.title,
    author: metadata.author,
    thumbnail: metadata.thumbnail,
    description,
    content,
    metadata: extraMetadata,
    hasTranscript: false // Be honest about this
  };
};
/**
 * METHOD 1: Extract transcript from YouTube's innertube API
 * This is what YouTube uses internally - most reliable client-side method
 */
export const fetchYouTubeTranscriptInnerTube = async (videoId) => {
  try {
    console.log('📝 Fetching transcript via InnerTube API for:', videoId);
    
    // Step 1: Get video page to extract player data
    const pageResponse = await fetch(
      `https://www.youtube.com/watch?v=${videoId}`
    );
    const pageHtml = await pageResponse.text();
    
    // Step 2: Extract ytInitialPlayerResponse
    const playerResponseMatch = pageHtml.match(/var ytInitialPlayerResponse = ({.+?});/);
    if (!playerResponseMatch) {
      console.log('⚠️ Could not find player response');
      return null;
    }
    
    const playerResponse = JSON.parse(playerResponseMatch[1]);
    
    // Step 3: Get caption tracks
    const captionTracks = playerResponse?.captions?.playerCaptionsTracklistRenderer?.captionTracks;
    
    if (!captionTracks || captionTracks.length === 0) {
      console.log('⚠️ No captions available for this video');
      return null;
    }
    
    // Step 4: Find English caption or first available
    const englishTrack = captionTracks.find(track => 
      track.languageCode === 'en' || track.languageCode === 'en-US'
    ) || captionTracks[0];
    
    if (!englishTrack.baseUrl) {
      console.log('⚠️ No caption URL found');
      return null;
    }
    
    console.log('✅ Found caption track:', englishTrack.name.simpleText);
    
    // Step 5: Fetch caption XML
    const captionResponse = await fetch(englishTrack.baseUrl);
    const captionXml = await captionResponse.text();
    
    // Step 6: Parse XML and extract text
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(captionXml, 'text/xml');
    const textElements = xmlDoc.getElementsByTagName('text');
    
    const transcript = Array.from(textElements)
      .map(element => element.textContent)
      .filter(text => text && text.trim().length > 0)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    console.log('✅ Transcript extracted:', transcript.length, 'characters');
    
    return transcript;
    
  } catch (error) {
    console.error('❌ InnerTube transcript extraction failed:', error);
    return null;
  }
};

/**
 * METHOD 2: Fallback using CORS proxy
 */
export const fetchYouTubeTranscriptWithProxy = async (videoId) => {
  try {
    console.log('📝 Trying CORS proxy method for:', videoId);
    
    // Using a CORS proxy service
    const proxyUrl = 'https://api.allorigins.win/raw?url=';
    const youtubeUrl = encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`);
    
    const response = await fetch(proxyUrl + youtubeUrl);
    const html = await response.text();
    
    // Extract caption tracks
    const captionMatch = html.match(/"captionTracks":\s*(\[.+?\])/);
    if (!captionMatch) {
      console.log('⚠️ No caption tracks found');
      return null;
    }
    
    const captionTracks = JSON.parse(captionMatch[1]);
    const englishTrack = captionTracks.find(t => t.languageCode === 'en') || captionTracks[0];
    
    if (!englishTrack || !englishTrack.baseUrl) {
      console.log('⚠️ No valid caption URL');
      return null;
    }
    
    // Fetch captions
    const captionResponse = await fetch(proxyUrl + encodeURIComponent(englishTrack.baseUrl));
    const captionXml = await captionResponse.text();
    
    // Parse XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(captionXml, 'text/xml');
    const textElements = xmlDoc.getElementsByTagName('text');
    
    const transcript = Array.from(textElements)
      .map(el => el.textContent)
      .filter(text => text && text.trim())
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    console.log('✅ Proxy transcript extracted:', transcript.length, 'characters');
    
    return transcript;
    
  } catch (error) {
    console.error('❌ Proxy transcript failed:', error);
    return null;
  }
};

/**
 * METHOD 3: Use youtube-transcript-api (client-side library)
 * Install: npm install youtube-transcript
 */
export const fetchTranscriptWithLibrary = async (videoId) => {
  try {
    // Import dynamically if needed
    const { YoutubeTranscript } = await import('youtube-transcript');
    
    const transcript = await YoutubeTranscript.fetchTranscript(videoId);
    const fullText = transcript.map(item => item.text).join(' ');
    
    console.log('✅ Library transcript extracted:', fullText.length, 'characters');
    return fullText;
    
  } catch (error) {
    console.error('❌ Library transcript failed:', error);
    return null;
  }
};

/**
 * Main function: Try multiple methods in order
 */
export const fetchYouTubeTranscript = async (videoId) => {
  console.log('📝 Starting transcript extraction for:', videoId);
  
  // Try Method 1: InnerTube API (most reliable)
  let transcript = await fetchYouTubeTranscriptInnerTube(videoId);
  if (transcript) {
    console.log('✅ Success with InnerTube method');
    return transcript;
  }
  
  // Try Method 2: CORS Proxy
  console.log('⚠️ InnerTube failed, trying CORS proxy...');
  transcript = await fetchYouTubeTranscriptWithProxy(videoId);
  if (transcript) {
    console.log('✅ Success with CORS proxy method');
    return transcript;
  }
  
  // Try Method 3: Client-side library (if installed)
  console.log('⚠️ CORS proxy failed, trying library...');
  try {
    transcript = await fetchTranscriptWithLibrary(videoId);
    if (transcript) {
      console.log('✅ Success with library method');
      return transcript;
    }
  } catch (error) {
    console.log('⚠️ Library method not available');
  }
  
  console.log('❌ All transcript methods failed');
  return null;
};


/**
 * Summarize long transcript (chunk and take key sections)
 */
export const summarizeTranscript = (transcript, maxLength = 2000) => {
  if (!transcript || transcript.length <= maxLength) {
    return transcript;
  }
  
  console.log('📝 Summarizing long transcript...');
  
  // Take beginning, middle, and end sections
  const chunkSize = Math.floor(maxLength / 3);
  const beginning = transcript.substring(0, chunkSize);
  const middle = transcript.substring(
    Math.floor(transcript.length / 2) - chunkSize / 2,
    Math.floor(transcript.length / 2) + chunkSize / 2
  );
  const end = transcript.substring(transcript.length - chunkSize);
  
  return `${beginning}\n\n[... middle content ...]\n\n${middle}\n\n[... later content ...]\n\n${end}`;
};

/**
 * Fallback: Use youtube-transcript library via API
 * This is more reliable but requires a backend endpoint
 */
export const fetchTranscriptViaAPI = async (videoId) => {
  try {
    // You would need to set up a simple backend endpoint that uses youtube-transcript npm package
    // Example backend (Node.js):
    /*
    const { YoutubeTranscript } = require('youtube-transcript');
    
    app.get('/api/transcript/:videoId', async (req, res) => {
      const transcript = await YoutubeTranscript.fetchTranscript(req.params.videoId);
      res.json({ transcript: transcript.map(t => t.text).join(' ') });
    });
    */
    
    const response = await fetch(`/api/transcript/${videoId}`);
    const data = await response.json();
    return data.transcript;
    
  } catch (error) {
    console.error('❌ API transcript fetch failed:', error);
    return null;
  }
};

/**
 * Crawl a single RSS source with timeout
 */
async function crawlSource(source, timeoutMs = 5000) {
  try {
    const text = await fetchWithProxy(source.url, timeoutMs);
    const items = parseRSSFeed(text, source.name);
    console.log(`✅ ${source.name}: ${items.length} recent articles`);
    return items;
  } catch (error) {
    if (error.message === 'Timeout') {
      console.log(`⏱️ ${source.name}: Timed out`);
    } else {
      console.log(`❌ ${source.name}: ${error.message}`);
    }
    return [];
  }
}

/**
 * Main search function - NOW USING BACKEND
 */
export const performWebSearch = async (query, count = 10) => {
  try {
    console.log(`🔍 Searching for: "${query}"`);
    
    // Check for YouTube
    const isYouTube = isYouTubeURL(query);
    if (isYouTube) {
      const videoId = extractYouTubeId(query);
      const youtubeData = await getCompleteYouTubeData(videoId);
      
      return {
        results: [{
          title: youtubeData.title,
          content: youtubeData.content,
          transcript: youtubeData.transcript,
          hasTranscript: youtubeData.hasTranscript
        }],
        isURLContent: true,
        isYouTube: true
      };
    }
    
    // Check for regular URL
    const detectedURL = detectURL(query);
    if (detectedURL) {
      console.log(`📄 Regular URL detected: ${detectedURL}`);
      
      // Use backend to fetch URL content
      const urlContent = await fetchWebContentBackend(detectedURL);
      
      if (urlContent.success) {
        return {
          query,
          totalResults: 1,
          results: [{
            title: urlContent.title,
            url: urlContent.url,
            snippet: urlContent.description || urlContent.content.substring(0, 200),
            content: urlContent.content,
            source: 'URL Content',
            datePublished: new Date().toISOString(),
            imageUrl: urlContent.image || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80'
          }],
          isURLContent: true,
          isYouTube: false,
          searchDate: new Date().toISOString()
        };
      }
    }
    
    // Use backend for news search
    console.log(`📰 Performing news search via backend...`);
    const searchResults = await performWebSearchBackend(query, count);
    
    if (!searchResults.success) {
      return {
        query,
        totalResults: 0,
        results: [],
        error: searchResults.error || 'Search failed',
        searchDate: new Date().toISOString()
      };
    }
    
    // Enrich with images (client-side)
    const enrichedResults = await enrichSearchResultsWithImages(
      searchResults.results, 
      8000
    );
    
    return {
      query,
      totalResults: searchResults.totalResults,
      results: enrichedResults,
      isURLContent: false,
      isYouTube: false,
      searchDate: searchResults.searchDate
    };
    
  } catch (error) {
    console.error('❌ Web search error:', error);
    return {
      query,
      totalResults: 0,
      results: [],
      error: 'Search failed. Please try again.',
      searchDate: new Date().toISOString()
    };
  }
};

/**
 * Format date for display
 */
export const formatSearchDate = (isoDate) => {
  try {
    const date = new Date(isoDate);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffTime / (1000 * 60));
      return diffMinutes <= 1 ? 'just now' : `${diffMinutes}m ago`;
    }
    if (diffHours < 24) return diffHours === 1 ? '1h ago' : `${diffHours}h ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays <= 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (e) {
    return 'recently';
  }
};

export default { performWebSearch, formatSearchDate, fetchURLContent };